import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

type AuthAwareConfig = InternalAxiosRequestConfig & { skipAuthRedirect?: boolean }

export const api = axios.create({
  baseURL: '/',
  withCredentials: true,
  headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
  timeout: 120_000,
})

let logoutInFlight = false

async function redirectToLogin(): Promise<void> {
  if (logoutInFlight) return
  logoutInFlight = true
  try {
    const { router, resetAuthChecked } = await import('@/router')
    resetAuthChecked()
    const path = window.location.pathname + window.location.search
    const redirect = encodeURIComponent(path)
    await router.replace({ name: 'login', query: { redirect } })
  } finally {
    window.setTimeout(() => {
      logoutInFlight = false
    }, 1500)
  }
}

function shouldReport(err: AxiosError): boolean {
  if (axios.isCancel(err)) return false
  const status = err.response?.status
  if (status === 401) return false
  const url = err.config?.url ?? ''
  if (url.includes('/api/user/client-errors')) return false
  if (!err.response) return true
  return status !== undefined && status >= 500
}

function reportAxiosFailure(err: AxiosError): void {
  if (!shouldReport(err)) return
  const status = err.response?.status
  const url = err.config?.url ?? ''
  const source = 'client_network' as const
  void import('./clientErrors')
    .then((m) =>
      m.reportClientError({
        message: err.message || 'network error',
        source,
        url,
        method: err.config?.method,
        status,
        stack: err.stack,
      }),
    )
    .catch(() => {})
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<{ message?: string; error_id?: string }>) => {
    reportAxiosFailure(err)
    const status = err.response?.status
    const config = err.config as AuthAwareConfig | undefined

    if (status !== 401 || config?.skipAuthRedirect) {
      return Promise.reject(err)
    }

    const url = config?.url ?? ''
    if (url.includes('/api/auth/') || url.includes('/api/user/profile')) {
      return Promise.reject(err)
    }

    const { router } = await import('@/router')
    if (router.currentRoute.value.name === 'login') {
      return Promise.reject(err)
    }

    // Сессия могла протухнуть только для одного эндпоинта — проверяем profile перед выходом.
    try {
      await api.get('/api/user/profile', { skipAuthRedirect: true } as AuthAwareConfig)
      return Promise.reject(err)
    } catch {
      await redirectToLogin()
      return Promise.reject(err)
    }
  },
)

export const PUBLIC_ERROR_MESSAGE = 'Произошла ошибка. Обратитесь в техническую поддержку.'

export function extractError(err: unknown, fallback = PUBLIC_ERROR_MESSAGE): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error_id?: string } | undefined
    const message = typeof data?.message === 'string' ? data.message.trim() : ''
    if (message) {
      const id = typeof data?.error_id === 'string' ? data.error_id.trim() : ''
      return id ? `${message} (код: ${id})` : message
    }
    return fallback
  }
  if (err instanceof Error) return fallback
  return fallback
}
